<div class="flex h-screen overflow-hidden bg-surface-0">
  <!-- Sidebar -->
  <nf-sidebar
    [(collapsed)]="layout.sidebarCollapsed"
    mode="light"
    [userRole]="user().role"
    [userName]="user().name"
    [userEmail]="user().email"
    (categoryClick)="onCategoryClick($event)"
    (userMenuAction)="onUserMenuAction($event)" />

  <!-- Content area -->
  <div class="flex flex-1 min-h-0 min-w-0 flex-col bg-sk-surface">
    @if (isLoading()) {
      <sk-progress [indeterminate]="true" size="sm" class="flex-shrink-0" />
    }
    <nf-topbar
      [featureName]="featureName()"
      [featureIcon]="featureIcon()"
      [featureId]="featureId()"
      [unreadCount]="bellCount()"
      [isDarkMode]="theme.isDarkMode()"
      (openSearch)="openSearch()"
      (openNotifs)="layout.notificationsOpen.set(true)"
      (toggleTheme)="theme.toggleTheme()" />

    <!--
      `main` (not the card below) owns scrolling, and stays a plain block — not a flex
      container — for the routed page's content. Two things break `position: sticky` on
      a header rendered deep inside that content: (a) an `overflow` other than `visible`
      on an ancestor between the sticky element and the actual scrolling element, since
      that ancestor becomes sticky's reference instead, and it never scrolls itself; and,
      less obviously, (b) nesting the sticky element an extra plain-block level inside a
      flex item chain, which is why `main` deliberately does NOT use `display: flex` here.
      The card below is a plain block wrapper: no `overflow-hidden` (must stay `visible`)
      and no `flex`/`flex-1` — it exists only to paint the rounded card look.
    -->
    <main class="flex-1 min-w-0 overflow-y-auto scrollbar-thin p-6 max-[760px]:p-3">
      <!--
        min-w-0 above (and on the card below) overrides the flex item's default
        min-width:auto, which otherwise lets a routed page's wide content (e.g. a
        horizontally-scrolling table) stretch `main` itself instead of scrolling
        inside its own container. Sizing-only change — does not touch `overflow`
        or `display`, so it doesn't affect the sticky-positioning notes above.
      -->
      <div
        class="min-h-full min-w-0 rounded-xl border border-sk-border nf-elevated [&:has(.nf-toolbelt-frame)]:!border-transparent [&:has(.nf-toolbelt-frame)]:!bg-transparent [&:has(.nf-toolbelt-frame)]:!shadow-none"
        style="background: var(--nf-tools-container-bg);"
      >
        <router-outlet />
      </div>
    </main>
  </div>

  <!-- LeapBot: floating window and the button with the quick menu (as in LSS) -->
  <nf-leapbot-chat
    [isOpen]="chatOpen() && leapbot.isEnabled()"
    (close)="closeChat()" />
  <nf-leapbot-fab
    [visible]="leapbot.isVisible()"
    [leapBotEnabled]="leapbot.isEnabled()"
    [chatOpen]="chatOpen()"
    (openChat)="openChat()" />
</div>

<!-- Admin Hub Modal -->
<nf-admin-hub-modal
  [isOpen]="adminHubOpen()"
  (cardSelect)="onAdminCardSelect($event)"
  (closeRequested)="adminHubOpen.set(false)" />


@if (languageOpen()) {
  <nf-language-dialog (closed)="languageOpen.set(false)" />
}

<!-- Notifications Drawer -->
<nf-notifications-drawer [(open)]="layout.notificationsOpen" />

<!-- What's new modal (Fury view over the stark-ui engine; mount: 'manual' in app.config.ts) -->
<nf-whats-new-modal />

<!-- Guided tour overlay -->
<sk-tour-overlay />

<!-- Server response modal: hard failures open it by themselves, the bell opens it on demand -->
<nf-server-response-modal
  mode="notification"
  [isOpen]="detailResponse() !== null"
  [title]="detailTitle()"
  [response]="detailResponse() ?? {}"
  [canAskLeapBot]="leapbot.isEnabled()"
  (askLeapBot)="askLeapBotAboutResponse()"
  (closed)="notifications.closeDetail()" />

<!-- Review Changes Dialog -->
<sk-review-changes-dialog
  [isOpen]="reviewChanges.isOpen()"
  [originalContent]="reviewChanges.originalContent()"
  [modifiedContent]="reviewChanges.modifiedContent()"
  [title]="reviewChanges.title()"
  [subtitle]="reviewChanges.subtitle()"
  [isSubmitting]="reviewChanges.isSubmitting()"
  [originalLabel]="'layout.reviewChanges.original' | transloco"
  [modifiedLabel]="'layout.reviewChanges.modified' | transloco"
  [savedLabel]="'layout.reviewChanges.saved' | transloco"
  [unsavedLabel]="'layout.reviewChanges.unsaved' | transloco"
  [cancelText]="'layout.reviewChanges.discard' | transloco"
  [confirmText]="'layout.reviewChanges.confirm' | transloco"
  [savingText]="'layout.reviewChanges.saving' | transloco"
  [footerInfo]="'layout.reviewChanges.footerInfo' | transloco"
  (discard)="reviewChanges.discard()"
  (confirm)="reviewChanges.confirm()" />

<sk-search-modal
  [title]="searchModalTitle()"
  [subtitle]="searchModalSubtitle()"
  [(isOpen)]="search.isOpen"
  [(activeTab)]="search.activeTab"
  [tabs]="searchModalTabs()"
  [items]="search.filteredItems()"
  [total]="search.filteredItems().length"
  [suggestions]="search.suggestions()"
  [maxSuggestions]="6"
  [renderItems]="true"
  [showFavorites]="true"
  size="xl"
  [searchPlaceholder]="searchModalPlaceholder()"
  [resultLabel]="searchModalResultLabel()"
  [resultsLabel]="searchModalResultsLabel()"
  [itemsPerPageLabel]="'layout.search.itemsPerPage' | transloco"
  [pageLabel]="'layout.search.page' | transloco"
  [ofLabel]="'layout.search.of' | transloco"
  [totalLabel]="'layout.search.total' | transloco"
  [closeLabel]="'layout.search.close' | transloco"
  [prevLabel]="'layout.search.previous' | transloco"
  [nextLabel]="'layout.search.next' | transloco"
  [noSuggestionsLabel]="'layout.search.noSuggestions' | transloco"
  [emptyTitle]="searchModalEmptyTitle()"
  [emptyDescription]="searchModalEmptyDescription()"
  (search)="onSearch($event)"
  (tabChange)="onSearchTabChange($event)"
  (suggestionSelect)="onSearchSuggestionSelect($event)"
  (itemSelect)="onSearchModalItemSelect($event)"
  (favoriteToggle)="toggleSearchItemFavorite($event)" />

<!-- Halloween layer: spiders, bats, witch, ghost and the mini-game creatures. Lazy chunk, only fetched while the mode is on. See nf-halloween/README.md to turn it off or remove it. -->
@defer (when halloween.enabled()) {
  @if (halloween.enabled()) {
    <nf-halloween-overlay />
  }
}
